import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCmsContent } from '../../hooks/useCmsContent';

type ServiceBoxProps = {
  title: string;
  description: string;
  isPrimary?: boolean;
};

const ServiceBox = ({ title, description, isPrimary = true }: ServiceBoxProps) => (
  <div className={`${isPrimary ? 'bg-primary/5 border-l-4 border-primary' : 'bg-secondary/5 border-l-4 border-secondary'} p-6 rounded-lg`}>
    <h4 className={`text-xl font-semibold ${isPrimary ? 'text-primary' : 'text-secondary'} mb-3`}>{title}</h4>
    <p className="text-gray-600">{description}</p>
  </div>
);

const AboutSection = () => {
  const { getContentValue, loading } = useCmsContent({ section: 'about' });
  
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-secondary uppercase tracking-wider font-semibold">La Nostra Storia</span>
            <h2 className="text-4xl font-monomakh text-primary mt-2 mb-6">Passione per le Case, <br/>Amore per le Persone</h2>
            <p className="text-gray-600 mb-6 text-lg leading-relaxed">
              Dal {getContentValue('anno_fondazione') || '2017'}, Molinaro Immobiliare trasforma case in luoghi da vivere. 
              La nostra profonda conoscenza di Scandicci non è solo professionale, ma personale: 
              conosciamo ogni angolo, ogni storia, ogni possibilità che questo territorio offre.
            </p>
            <p className="text-gray-600 mb-8 text-lg leading-relaxed">
              Da dicembre 2024 abbiamo scelto l'indipendenza per darti un servizio ancora più 
              autentico e personale, dove ogni cliente è unico e ogni casa ha una storia da raccontare.
            </p>
            <Link 
              to="/chi-siamo"
              className="inline-flex items-center text-secondary hover:text-secondary-light font-semibold text-lg group"
            >
              Scopri la nostra storia
              <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-2 gap-6"
          >
            <div className="space-y-6">
              <ServiceBox 
                title="Valutazioni Gratuite" 
                description="Un'analisi professionale per conoscere il vero valore del tuo immobile." 
                isPrimary={true}
              />
              <ServiceBox 
                title="Consulenza Legale" 
                description="Ti guidiamo tra i dettagli legali con chiarezza e professionalità." 
                isPrimary={false}
              />
            </div>
            <div className="space-y-6 mt-10">
              <ServiceBox 
                title="Mutui Personalizzati" 
                description="Soluzioni finanziarie cucite sulle tue esigenze e possibilità." 
                isPrimary={false}
              />
              <ServiceBox 
                title="Assistenza Continua" 
                description="Dalla prima visita al rogito, sempre al tuo fianco con esperienza." 
                isPrimary={true}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;